import { useState, useEffect, useCallback } from "react";
import { 
  FileText, DollarSign, CreditCard, Calendar, Building2, 
  Download, Upload, Eye, CheckCircle2, XCircle, Clock,
  ChevronDown, ChevronUp, Banknote, Receipt, AlertTriangle, UserPlus,
  Trash2, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

const PAYMENT_STATUS_CONFIG = {
  no_pagado: { label: "NO PAGADO", color: "bg-red-100 text-red-800", icon: XCircle },
  pagado: { label: "PAGADO", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
};

const STATUS_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800" },
  en_proceso: { label: "En Proceso", color: "bg-blue-100 text-blue-800" },
  completado: { label: "Completado", color: "bg-green-100 text-green-800" },
  cancelado: { label: "Cancelado", color: "bg-gray-100 text-gray-800" },
};

const BANK_ACCOUNTS = [
  { id: "fondeadora", name: "BANCO FONDEADORA" },
  { id: "kapital", name: "KAPITAL BANK" },
];

export default function PreTramitesTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPayment, setFilterPayment] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  
  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPretramite, setSelectedPretramite] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_status: "pagado",
    payment_date: new Date().toISOString().split('T')[0],
    bank_account: "",
    payment_notes: "",
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pretramiteToDelete, setPretramiteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Get user role  
  const [userRole, setUserRole] = useState("admin");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pretramites`, axCfg);
      setData(res.data);
    } catch (e) {
      toast.error("Error al cargar pre-tramites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter data
  const filteredData = data.filter(pt => {
    if (filterPayment === "sin_cliente") {
      return pt.payment_status === "pagado" && !pt.client_id;
    }
    if (filterPayment !== "all" && pt.payment_status !== filterPayment) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      pt.orden_number?.toLowerCase().includes(searchLower) ||
      pt.prospect_name?.toLowerCase().includes(searchLower) ||
      pt.prospect_company?.toLowerCase().includes(searchLower) ||
      pt.quotation_number?.toLowerCase().includes(searchLower)
    );
  });

  const openPaymentModal = (pretramite) => {
    setSelectedPretramite(pretramite);
    setPaymentForm({
      payment_status: "pagado",
      payment_date: pretramite.payment_info?.date || new Date().toISOString().split('T')[0],
      bank_account: pretramite.payment_info?.bank_account || "",
      payment_notes: pretramite.payment_info?.notes || "",
    });
    setPaymentProofFile(null);
    setPaymentModalOpen(true);
  };

  const openDeleteModal = (pretramite) => {
    setPretramiteToDelete(pretramite);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pretramiteToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/pretramites/${pretramiteToDelete.id}`, axCfg);
      toast.success("Pre-trámite eliminado correctamente");
      setDeleteModalOpen(false);
      setPretramiteToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar pre-trámite");
    } finally {
      setDeleting(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentForm.bank_account) {
      toast.error("Selecciona la cuenta bancaria");
      return;
    }
    
    setUploading(true);
    try {
      // Update payment status
      const formData = new FormData();
      formData.append("payment_status", paymentForm.payment_status);
      formData.append("payment_date", paymentForm.payment_date);
      formData.append("bank_account", paymentForm.bank_account);
      formData.append("payment_notes", paymentForm.payment_notes);
      
      const response = await axios.patch(
        `${API}/admin/pretramites/${selectedPretramite.id}/payment`,
        formData,
        { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      // Upload payment proof if provided
      if (paymentProofFile) {
        const proofFormData = new FormData();
        proofFormData.append("file", paymentProofFile);
        await axios.post(
          `${API}/admin/pretramites/${selectedPretramite.id}/payment-proof`,
          proofFormData,
          { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      
      // Show appropriate message
      if (response.data.tramite_created) {
        toast.success("Pago registrado. ¡Trámite creado automáticamente! Ve a la pestaña 'Tramites' para verlo.");
      } else {
        toast.success("Pago registrado correctamente");
      }
      
      setPaymentModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar pago");
    } finally {
      setUploading(false);
    }
  };

  const downloadPaymentProof = async (pretramiteId) => {
    try {
      const response = await axios.get(
        `${API}/admin/pretramites/${pretramiteId}/payment-proof/download`,
        { ...axCfg, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'comprobante_pago');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar comprobante");
    }
  };

  const updateStatus = async (pretramiteId, status) => {
    try {
      const formData = new FormData();
      formData.append("status", status);
      await axios.patch(
        `${API}/admin/pretramites/${pretramiteId}/status`,
        formData,
        { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Estado actualizado");
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar estado");
    }
  };

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando pre-tramites...</p>;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <Input
          placeholder="Buscar por orden, cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="search-pretramites"
        />
        <Select value={filterPayment} onValueChange={setFilterPayment}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Estado de pago" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="no_pagado">No Pagado</SelectItem>
            <SelectItem value="pagado">Pagado</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500">{filteredData.length} ordenes</span>
      </div>

      {/* Summary Cards - Clickeable para filtrar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterPayment === 'all' ? 'ring-2 ring-[#1a5c4a]' : ''}`}
          onClick={() => setFilterPayment('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1a5c4a] flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.length}</p>
                <p className="text-xs text-gray-600">Total Ordenes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterPayment === 'no_pagado' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilterPayment('no_pagado')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.filter(d => d.payment_status === "no_pagado").length}</p>
                <p className="text-xs text-gray-600">No Pagados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterPayment === 'pagado' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilterPayment('pagado')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.filter(d => d.payment_status === "pagado").length}</p>
                <p className="text-xs text-gray-600">Pagados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterPayment === 'sin_cliente' ? 'ring-2 ring-amber-400' : ''} ${data.filter(d => d.payment_status === "pagado" && !d.client_id).length > 0 ? 'ring-2 ring-amber-400 animate-pulse' : ''}`}
          onClick={() => setFilterPayment('sin_cliente')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.filter(d => d.payment_status === "pagado" && !d.client_id).length}</p>
                <p className="text-xs text-gray-600">Sin Cliente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ${data.filter(d => d.payment_status === "pagado").reduce((sum, d) => sum + (d.total || 0), 0).toLocaleString('es-MX')}
                </p>
                <p className="text-xs text-gray-600">Total Cobrado</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pre-tramites List */}
      {filteredData.length === 0 ? (
        <Card className="border-none">
          <CardContent className="text-center py-16">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay ordenes de servicio {search ? "con ese criterio" : ""}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredData.map((pt) => {
            const paymentConfig = PAYMENT_STATUS_CONFIG[pt.payment_status] || PAYMENT_STATUS_CONFIG.no_pagado;
            const statusConfig = STATUS_CONFIG[pt.status] || STATUS_CONFIG.pendiente;
            const PaymentIcon = paymentConfig.icon;
            const isExpanded = expandedId === pt.id;

            return (
              <Card key={pt.id} className="border-none shadow-sm" data-testid={`pretramite-${pt.id}`}>
                <CardContent className="p-4">
                  {/* Main Row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{pt.orden_number}</h3>
                        <Badge className={paymentConfig.color}>
                          <PaymentIcon className="h-3 w-3 mr-1" />
                          {paymentConfig.label}
                        </Badge>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        {/* Alert badge for paid without client */}
                        {pt.payment_status === "pagado" && !pt.client_id && (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Sin Cliente Registrado
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600 mb-2 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {pt.prospect_company || pt.prospect_name || "Sin nombre"}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {pt.quotation_number}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(pt.created_at).toLocaleDateString('es-MX')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-[#1a5c4a]">
                          ${pt.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-xs text-gray-500">
                          (Subtotal: ${pt.subtotal?.toLocaleString('es-MX')} + IVA: ${pt.iva?.toLocaleString('es-MX')})
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pt.payment_status === "no_pagado" ? (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openPaymentModal(pt)}
                          data-testid={`register-payment-${pt.id}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Registrar Pago
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300"
                          onClick={() => openPaymentModal(pt)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Pago
                        </Button>
                      )}
                      <Select value={pt.status} onValueChange={(v) => updateStatus(pt.id, v)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="en_proceso">En Proceso</SelectItem>
                          <SelectItem value="completado">Completado</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpandedId(isExpanded ? null : pt.id)}
                        className="h-8 w-8"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                      {userRole === "admin" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => openDeleteModal(pt)}
                          title="Eliminar pre-trámite"
                          data-testid={`delete-pretramite-${pt.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Items */}
                        <div>
                          <h4 className="font-medium text-sm mb-2">Servicios Cotizados</h4>
                          <div className="space-y-2">
                            {pt.items?.map((item, idx) => (
                              <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                                <div className="flex justify-between">
                                  <span className="font-medium">{item.service_name}</span>
                                  <span>${(item.quantity * item.unit_price).toLocaleString('es-MX')}</span>
                                </div>
                                {item.description && (
                                  <p className="text-gray-500 mt-1">{item.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Payment Info */}
                        {pt.payment_status === "pagado" && pt.payment_info && (
                          <div>
                            <h4 className="font-medium text-sm mb-2">Informacion de Pago</h4>
                            <div className="bg-green-50 p-3 rounded space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Fecha de Pago:</span>
                                <span className="font-medium">{pt.payment_info.date}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Cuenta:</span>
                                <span className="font-medium">
                                  {BANK_ACCOUNTS.find(b => b.id === pt.payment_info.bank_account)?.name || pt.payment_info.bank_account}
                                </span>
                              </div>
                              {pt.payment_info.notes && (
                                <div>
                                  <span className="text-gray-600">Notas:</span>
                                  <p className="font-medium mt-1">{pt.payment_info.notes}</p>
                                </div>
                              )}
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Registrado por: {pt.payment_info.registered_by}</span>
                              </div>
                              {pt.payment_proof && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full mt-2"
                                  onClick={() => downloadPaymentProof(pt.id)}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Descargar Comprobante
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {pt.notes && (
                        <div className="mt-4">
                          <h4 className="font-medium text-sm mb-1">Notas</h4>
                          <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{pt.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-green-600" />
              {selectedPretramite?.payment_status === "pagado" ? "Ver Pago" : "Registrar Pago"}
            </DialogTitle>
            <DialogDescription>
              Orden: {selectedPretramite?.orden_number} - Total: ${selectedPretramite?.total?.toLocaleString('es-MX')}
            </DialogDescription>
          </DialogHeader>

          {/* VIEW PAYMENT MODE */}
          {selectedPretramite?.payment_status === "pagado" && selectedPretramite?.payment_info ? (
            <div className="space-y-4 py-4">
              {/* Payment Info Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-800 font-medium">
                  <CheckCircle2 className="h-5 w-5" />
                  Pago Registrado
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Fecha de Pago</p>
                    <p className="font-medium">{selectedPretramite.payment_info.date}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Cuenta Bancaria</p>
                    <p className="font-medium">
                      {BANK_ACCOUNTS.find(b => b.id === selectedPretramite.payment_info.bank_account)?.name || selectedPretramite.payment_info.bank_account}
                    </p>
                  </div>
                </div>
                
                {selectedPretramite.payment_info.notes && (
                  <div className="text-sm">
                    <p className="text-gray-600">Notas</p>
                    <p className="font-medium">{selectedPretramite.payment_info.notes}</p>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 pt-2 border-t border-green-200">
                  Registrado por: {selectedPretramite.payment_info.registered_by} el{" "}
                  {new Date(selectedPretramite.payment_info.registered_at).toLocaleString('es-MX')}
                </div>
              </div>

              {/* Payment Proof Section */}
              <div className="space-y-2">
                <Label className="font-medium">Comprobante de Pago</Label>
                {selectedPretramite.payment_proof ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1a5c4a]/10 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-[#1a5c4a]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{selectedPretramite.payment_proof.original_filename}</p>
                          <p className="text-xs text-gray-500">
                            Subido el {new Date(selectedPretramite.payment_proof.uploaded_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#1a5c4a] hover:bg-[#143f34]"
                        onClick={() => downloadPaymentProof(selectedPretramite.id)}
                        data-testid="download-proof-btn"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Descargar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-amber-800 font-medium">Sin comprobante cargado</p>
                    <p className="text-xs text-amber-600 mt-1">Puede subir el comprobante a continuación</p>
                    
                    {/* Allow uploading proof even after payment is registered */}
                    <div className="mt-3">
                      <input
                        id="payment_proof_view"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setPaymentProofFile(e.target.files[0])}
                        className="hidden"
                      />
                      <label
                        htmlFor="payment_proof_view"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg cursor-pointer text-sm font-medium transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        {paymentProofFile ? paymentProofFile.name : "Subir Comprobante"}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Client Status */}
              {!selectedPretramite.client_id && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Cliente no registrado</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Este Pre-Trámite está pagado pero no tiene un cliente asociado. 
                    Vaya a la pestaña "Clientes" para registrarlo.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* REGISTER PAYMENT MODE */
            <div className="space-y-4 py-4">
              {/* Payment Date */}
              <div className="space-y-2">
                <Label htmlFor="payment_date">Fecha de Pago</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
              </div>

              {/* Bank Account */}
              <div className="space-y-2">
                <Label htmlFor="bank_account">Cuenta Bancaria *</Label>
                <Select
                  value={paymentForm.bank_account}
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, bank_account: v })}
                >
                  <SelectTrigger data-testid="bank-account-select">
                    <SelectValue placeholder="Selecciona la cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANK_ACCOUNTS.map((bank) => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Proof Upload */}
              <div className="space-y-2">
                <Label htmlFor="payment_proof">Comprobante de Pago (PDF, JPG, PNG)</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    id="payment_proof"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setPaymentProofFile(e.target.files[0])}
                    className="hidden"
                    data-testid="payment-proof-input"
                  />
                  <label
                    htmlFor="payment_proof"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    {paymentProofFile ? (
                      <span className="text-sm text-green-600 font-medium">{paymentProofFile.name}</span>
                    ) : (
                      <span className="text-sm text-gray-500">Clic para subir comprobante</span>
                    )}
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="payment_notes">Notas (opcional)</Label>
                <Textarea
                  id="payment_notes"
                  placeholder="Ej: Transferencia SPEI, referencia #12345"
                  value={paymentForm.payment_notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_notes: e.target.value })}
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>
              {selectedPretramite?.payment_status === "pagado" ? "Cerrar" : "Cancelar"}
            </Button>
            {selectedPretramite?.payment_status !== "pagado" && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handlePaymentSubmit}
                disabled={uploading}
                data-testid="submit-payment-btn"
              >
                {uploading ? "Guardando..." : "Registrar Pago"}
              </Button>
            )}
            {/* Button to upload proof after viewing */}
            {selectedPretramite?.payment_status === "pagado" && paymentProofFile && (
              <Button
                className="bg-[#1a5c4a] hover:bg-[#143f34]"
                onClick={async () => {
                  setUploading(true);
                  try {
                    const proofFormData = new FormData();
                    proofFormData.append("file", paymentProofFile);
                    await axios.post(
                      `${API}/admin/pretramites/${selectedPretramite.id}/payment-proof`,
                      proofFormData,
                      { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
                    );
                    toast.success("Comprobante subido correctamente");
                    setPaymentModalOpen(false);
                    setPaymentProofFile(null);
                    fetchData();
                  } catch (e) {
                    toast.error("Error al subir comprobante");
                  } finally {
                    setUploading(false);
                  }
                }}
                disabled={uploading}
              >
                {uploading ? "Subiendo..." : "Guardar Comprobante"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Pre-Trámite
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar esta orden de servicio?
            </DialogDescription>
          </DialogHeader>

          {pretramiteToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="font-mono font-bold text-[#1a5c4a]">{pretramiteToDelete.orden_number}</p>
                <p className="text-sm font-medium">{pretramiteToDelete.prospect_name}</p>
                <p className="text-sm text-gray-500">{pretramiteToDelete.prospect_company}</p>
                <p className="text-sm text-gray-500">Cotización: {pretramiteToDelete.quotation_number}</p>
              </div>
              
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">Se cancelará:</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Monto: <strong>${pretramiteToDelete.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong></li>
                  {pretramiteToDelete.payment_status === "pagado" && (
                    <li className="text-red-800 font-semibold">⚠️ Este pago ya fue registrado y se restará del total cobrado</li>
                  )}
                  <li>La orden de servicio será eliminada permanentemente</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteModalOpen(false);
                setPretramiteToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sí, Eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
